import { EyeOff } from "@procore/core-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { useAbortController } from "hooks/useAbortController";
import {
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
} from "utils/objectActions";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";

type VisibilityToggleButtonProps = {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds?: Set<number>;
};

export function VisibilityToggleButton({ node, tree, filteredObjectIds }: VisibilityToggleButtonProps) {
    const { t } = useTranslation();
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);
    const hidden = useHidden();
    const dispatchHidden = useDispatchHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;
    const [abortController] = useAbortController();
    const [isToggling, setIsToggling] = useState(false);

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(
                filteredObjectIds
                    ? getGroupStateForFilteredTree(isolationVisibleIds, item, filteredObjectIds, tree)
                    : getGroupStateForIdSet(isolationVisibleIds, item, tree),
            );
        }
        return filteredObjectIds
            ? getGroupStateForFilteredTree(hidden.ids, item, filteredObjectIds, tree)
            : getGroupStateForIdSet(hidden.ids, item, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree, filteredObjectIds]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));
    const shouldShow = isHidden || hiddenState === "some";

    const handleToggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isToggling) {
            return;
        }
        if (shouldShow) {
            if (isolationVisibleIds) {
                await isolation.setHierarchyVisible({
                    nodes: [node.data.object],
                    visible: true,
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    filteredObjectIds,
                });
                return;
            }
            if (filteredObjectIds) {
                await showObjectHierarchyFromFilteredTree({
                    nodes: [node.data.object],
                    filteredObjectIds,
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    dispatchHidden,
                });
            } else {
                await showObjectHierarchy({
                    nodes: [node.data.object],
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    dispatchHidden,
                });
            }
        } else {
            if (isolationVisibleIds) {
                await isolation.setHierarchyVisible({
                    nodes: [node.data.object],
                    visible: false,
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    filteredObjectIds,
                });
                return;
            }
            if (filteredObjectIds) {
                await hideObjectHierarchyFromFilteredTree({
                    nodes: [node.data.object],
                    filteredObjectIds,
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    dispatchHidden,
                });
            } else {
                await hideObjectHierarchy({
                    nodes: [node.data.object],
                    dataAccess,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                    dispatchHidden,
                });
            }
        }
    };

    return (
        <>
            {!isHidden && hiddenState === "some" && (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        flexShrink: 0,
                        paddingTop: 2,
                        paddingBottom: 2,
                        paddingLeft: 6,
                        paddingRight: 8,
                        borderRadius: 9999,
                        background: "#eef0f1",
                        border: "1px solid #acb5b9",
                    }}
                >
                    <EyeOff style={{ width: 12, height: 12, flexShrink: 0, color: "#5e696e" }} aria-hidden="true" />
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: "16px",
                            color: "#5e696e",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("someHidden")}
                    </span>
                </span>
            )}
            <button
                type="button"
                aria-label={shouldShow ? t("showObject") : t("hideObject")}
                onClick={(e) => void handleToggle(e)}
                style={{
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                    opacity: isToggling ? 0.4 : 1,
                    pointerEvents: isToggling ? "none" : "auto",
                }}
                tabIndex={-1}
            >
                <TogglePill on={!isHidden && hiddenState !== "some"} />
            </button>
        </>
    );
}

function TogglePill({ on }: { on: boolean }) {
    return (
        <div
            style={{
                width: 44,
                height: 26,
                borderRadius: 13,
                backgroundColor: on ? "#007AFF" : "#E5E5EA",
                position: "relative",
                flexShrink: 0,
                transition: "background-color 0.2s ease",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    top: 2,
                    left: on ? 20 : 2,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                    transition: "left 0.2s ease",
                }}
            />
        </div>
    );
}
